import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { KeyRound, ShieldOff, RefreshCw, MessageCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "resolved", label: "Resolved" },
  { id: "dismissed", label: "Dismissed" },
];

export default function AdminResets() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(null); // request being actioned

  const load = () => {
    setLoading(true);
    return api
      .get("/admin/password-resets", { params: { status: "all", _: Date.now() } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter((i) => i.status === tab);

  const dismiss = async (req) => {
    if (!window.confirm(`Dismiss reset request from ${req.email}?`)) return;
    try {
      await api.post(`/admin/password-resets/${req.id}/dismiss`);
      toast.success("Request dismissed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-5">
      {/* Tab strip */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 p-1 rounded-xl border border-zinc-800 bg-zinc-950/60">
          {TABS.map((t) => {
            const count = items.filter((i) => i.status === t.id).length;
            return (
              <button
                key={t.id}
                data-testid={`resets-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.18em] transition-colors ${
                  tab === t.id
                    ? "bg-emerald-500/10 text-emerald-300"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {t.label}
                <span
                  className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                    tab === t.id
                      ? "bg-emerald-500/15 text-emerald-200"
                      : "bg-zinc-900 text-zinc-500"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <button
          data-testid="resets-refresh-btn"
          onClick={load}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.18em] border border-zinc-800 bg-zinc-950/60 text-zinc-300 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="text-zinc-500 text-sm">Loading reset requests...</div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-20 border border-dashed border-zinc-800 rounded-2xl">
          <KeyRound className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <div className="font-display text-xl tracking-tight">
            No {tab} requests
          </div>
          <div className="text-sm text-zinc-500 mt-1">
            {tab === "pending"
              ? "When a member taps 'Forgot password?' on login, their request will appear here."
              : "Older actioned requests live here for your records."}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {filtered.map((r) => (
          <RequestCard
            key={r.id}
            r={r}
            onReset={() => setResetting(r)}
            onDismiss={() => dismiss(r)}
          />
        ))}
      </div>

      {resetting && (
        <ResetDialog
          req={resetting}
          onClose={() => setResetting(null)}
          onDone={() => {
            setResetting(null);
            load();
          }}
        />
      )}
    </div>
  );
}

const RequestCard = ({ r, onReset, onDismiss }) => {
  const isPending = r.status === "pending";
  return (
    <div
      data-testid={`reset-row-${r.id}`}
      className="border border-zinc-900 rounded-2xl bg-zinc-950/40 p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            Reset request
          </div>
          <div className="font-display text-xl tracking-tight font-bold text-white mt-1 truncate">
            {r.name || r.email}
          </div>
          <div className="text-sm text-zinc-500 truncate">{r.email}</div>
          {r.whatsapp && (
            <a
              href={`https://wa.me/${r.whatsapp.replace(/[^\d]/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-1 text-xs text-emerald-300 hover:text-emerald-200"
            >
              <MessageCircle className="w-3 h-3" />
              {r.whatsapp}
            </a>
          )}
        </div>
        <span
          className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border whitespace-nowrap ${
            r.status === "pending"
              ? "text-amber-300 border-amber-500/30 bg-amber-500/5"
              : r.status === "resolved"
              ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/5"
              : "text-zinc-400 border-zinc-700"
          }`}
        >
          {r.status}
        </span>
      </div>

      {r.message && (
        <div className="mt-3 text-sm text-zinc-300 border-l-2 border-zinc-800 pl-3 italic">
          "{r.message}"
        </div>
      )}

      <div className="mt-3 text-[11px] text-zinc-600">
        Filed{" "}
        {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
        {r.resolved_at && (
          <>
            {" · resolved "}
            {new Date(r.resolved_at).toLocaleString()}
            {r.resolved_by && ` by ${r.resolved_by}`}
          </>
        )}
      </div>

      {isPending && (
        <div className="mt-4 flex items-center gap-2">
          <button
            data-testid={`reset-action-${r.id}`}
            onClick={onReset}
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
          >
            <KeyRound className="w-3.5 h-3.5" /> Set new password
          </button>
          <button
            data-testid={`reset-dismiss-${r.id}`}
            onClick={onDismiss}
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-zinc-800 text-zinc-400 hover:text-rose-300 hover:border-rose-500/30 transition-colors"
          >
            <ShieldOff className="w-3.5 h-3.5" /> Dismiss
          </button>
        </div>
      )}
    </div>
  );
};

const generatePassword = () => {
  const chars =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  const arr = window.crypto?.getRandomValues
    ? window.crypto.getRandomValues(new Uint32Array(12))
    : null;
  for (let i = 0; i < 12; i++) {
    const idx = arr ? arr[i] % chars.length : Math.floor(Math.random() * chars.length);
    out += chars[idx];
  }
  return out + "@1";
};

const ResetDialog = ({ req, onClose, onDone }) => {
  const [pw, setPw] = useState(generatePassword());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${req.user_id}/reset-password`, {
        new_password: pw,
        request_id: req.id,
      });
      setDone(true);
      toast.success("Password reset — share with user");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset password");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  const waText = encodeURIComponent(
    `Hi ${req.name || ""}, your Business Breakthrough Hub password has been reset.\n\nTemporary password: ${pw}\n\nLogin at the hub and you'll be asked to set a new password right away.`
  );
  const waLink = req.whatsapp
    ? `https://wa.me/${req.whatsapp.replace(/[^\d]/g, "")}?text=${waText}`
    : null;

  return (
    <div
      data-testid="reset-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6 grain">
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80">
          // password reset
        </div>
        <h2 className="font-display text-2xl tracking-tighter font-black mt-1">
          {req.name || req.email}
        </h2>
        <div className="text-sm text-zinc-500 mb-5">{req.email}</div>

        {!done ? (
          <>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">
              New temporary password
            </div>
            <div className="flex items-center gap-2 mb-4">
              <input
                data-testid="reset-pw-input"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-white font-mono text-sm outline-none focus:border-emerald-500/50"
              />
              <button
                onClick={() => setPw(generatePassword())}
                className="text-[10px] uppercase tracking-[0.2em] px-3 py-2.5 rounded-lg border border-zinc-800 text-zinc-400 hover:text-emerald-300 hover:border-emerald-500/30"
                title="Generate new"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-xs text-zinc-500">
              The user will be prompted to change this on their next login.
              Their existing JWT tokens will be invalidated immediately.
            </p>

            <div className="mt-6 flex items-center gap-2 justify-end">
              <button
                onClick={onClose}
                disabled={busy}
                className="text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-md border border-zinc-800 text-zinc-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                data-testid="reset-confirm-btn"
                onClick={submit}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
              >
                {busy ? "Saving…" : "Reset & save"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 mb-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 mb-2">
                Share with user (one-time)
              </div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="reset-pw-display"
                  className="flex-1 text-emerald-200 font-mono text-sm bg-zinc-950/60 border border-zinc-800 rounded px-2 py-1.5 break-all"
                >
                  {pw}
                </code>
                <button
                  onClick={copy}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-3 py-2 rounded border border-zinc-800 text-zinc-300 hover:text-emerald-300 hover:border-emerald-500/30"
                  data-testid="reset-pw-copy"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
            {waLink && (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reset-pw-whatsapp"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors glow-primary"
              >
                <MessageCircle className="w-4 h-4" />
                Send via WhatsApp
              </a>
            )}
            <button
              onClick={onDone}
              data-testid="reset-pw-done"
              className="w-full mt-3 text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white"
            >
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
};
